<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useAnalyticsStore } from '@/stores/analytics'
import { useAuthStore } from '@/stores/auth'
import { useCustomerStore } from '@/stores/customer'
import { usePaymentMethodStore } from '@/stores/paymentMethod'
import { app } from '@/firebase'

const analyticsStore = useAnalyticsStore()
const authStore = useAuthStore()
const customerStore = useCustomerStore()
const paymentMethodStore = usePaymentMethodStore()

const isSendingTest = ref(false)
const isIdentifying = ref(false)
const isModalOpen = ref(false)
const debugUserId = ref('')
const debugUserProperties = ref('{}')

// Get status info
const status = computed(() => {
  return {
    isEnabled: analyticsStore.isEnabled,
    isAvailable: analyticsStore.isAvailable,
    projectId: app.options.projectId,
    measurementId: app.options.measurementId || 'Not configured',
  }
})

// Get user info
const userInfo = computed(() => {
  return {
    userId: analyticsStore.userId,
    properties: analyticsStore.userProperties,
  }
})

// Get recent events
const recentEvents = computed(() => {
  return analyticsStore.recentEvents
})

// Send test event
async function sendTestEvent() {
  isSendingTest.value = true
  try {
    // Use store's wrapped events which preserve types and record to recentEvents
    await analyticsStore.events.trackSelectContent({
      content_type: 'test_event',
      item_id: 'debug_test',
    })
  } catch (error) {
    console.error('Failed to send test event:', error)
  } finally {
    isSendingTest.value = false
  }
}

// Clear recent events
function clearEvents() {
  analyticsStore.clearRecentEvents()
}

// Open identification modal
function openIdentifyModal() {
  prefillFromSystem()
  isModalOpen.value = true
}

// Close identification modal
function closeIdentifyModal() {
  isModalOpen.value = false
}

// Identify user manually
async function identifyUser() {
  if (!debugUserId.value) return

  isIdentifying.value = true
  try {
    let props = {}
    if (debugUserProperties.value && debugUserProperties.value.trim() !== '{}') {
      try {
        props = JSON.parse(debugUserProperties.value)
      } catch (e) {
        console.error('Failed to parse user properties JSON:', e)
        alert('Invalid JSON in User Properties')
        return
      }
    }
    await analyticsStore.setUser(debugUserId.value, props, true)
    closeIdentifyModal()
    alert('User identified successfully!')
  } catch (error) {
    console.error('Failed to identify user:', error)
    alert('Failed to identify user: ' + error)
  } finally {
    isIdentifying.value = false
  }
}

// Format timestamp
function formatTimestamp(timestamp: number): string {
  const date = new Date(timestamp)
  const hours = date.getHours().toString().padStart(2, '0')
  const minutes = date.getMinutes().toString().padStart(2, '0')
  const seconds = date.getSeconds().toString().padStart(2, '0')
  const ms = date.getMilliseconds().toString().padStart(3, '0')
  return `${hours}:${minutes}:${seconds}.${ms}`
}

// Format date
function formatDate(timestamp: number): string {
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).format(timestamp)
}

// Initialize analytics store on mount
onMounted(async () => {
  await analyticsStore.initialize()
  prefillFromSystem()
})

// Watch for changes in auth/customer to update prefill if fields haven't been touched
watch([() => authStore.user, () => customerStore.customer], () => {
  if (!debugUserId.value) {
    prefillFromSystem()
  }
})

/**
 * Prefill manual identification fields with what the system would identify
 */
function prefillFromSystem() {
  if (authStore.user) {
    debugUserId.value = authStore.user.uid
    const properties = {
      user_role: authStore.userRole || 'customer',
      is_test_customer: String(authStore.isTestCustomer),
      subscription_status: paymentMethodStore.subscriptionStatus || 'none',
      favourite_classes_count: customerStore.customer?.favouriteClasses?.length || 0,
      has_enrolled: String(!!customerStore.customer?.hasEnrolled),
      state_of_residence: customerStore.customer?.stateOfResidence || 'unknown',
      gender: customerStore.customer?.gender || 'Unknown',
      hear_about_us: customerStore.customer?.hearAboutUs || 'unknown',
      signup_flow_completed: String(!!customerStore.customer?.signupFlowCompleted),
      lead_question_answer: customerStore.customer?.leadQuestionAnswer || 'none',
    }
    debugUserProperties.value = JSON.stringify(properties, null, 2)
  }
}
</script>

<template>
  <div class="analytics-debug">
    <!-- Status Section -->
    <div class="config-section">
      <h4 class="section-title">Analytics Status</h4>
      <div class="status-grid">
        <div class="status-item">
          <span class="status-label">Enabled:</span>
          <span :class="['status-value', status.isEnabled ? 'text-green-600' : 'text-red-600']">
            {{ status.isEnabled ? 'Yes' : 'No' }}
          </span>
        </div>
        <div class="status-item">
          <span class="status-label">Available:</span>
          <span :class="['status-value', status.isAvailable ? 'text-green-600' : 'text-red-600']">
            {{ status.isAvailable ? 'Yes' : 'No' }}
          </span>
        </div>
        <div class="status-item">
          <span class="status-label">Project ID:</span>
          <span class="status-value text-gray-600 font-mono text-xs">
            {{ status.projectId }}
          </span>
        </div>
        <div class="status-item">
          <span class="status-label">Measurement ID:</span>
          <span class="status-value text-gray-600 font-mono text-xs">
            {{ status.measurementId }}
          </span>
        </div>
      </div>

      <div v-if="!status.isEnabled" class="warning-box">
        <strong>Analytics not enabled</strong>
        <p class="text-sm mt-1">Enable Analytics in Firebase Console → Analytics → Get Started</p>
      </div>

      <div v-else class="info-box mt-2">
        <strong>GA4 Debug View</strong>
        <p class="text-sm mt-1">
          For test users we set <code>debug_mode</code> in the tag config so events appear in GA4 →
          Admin → DebugView. If events still don’t show: try the
          <a
            href="https://chrome.google.com/webstore/detail/google-analytics-debugger/jnkmfdileelhofjcijamephohjechhna"
            target="_blank"
            rel="noopener"
            >Google Analytics Debugger</a
          >
          extension, disable ad blockers on this origin, and ensure you’re in the correct GA4
          property.
        </p>
      </div>
    </div>

    <!-- User Info Section -->
    <div class="config-section">
      <h4 class="section-title">User Tracking</h4>

      <div v-if="userInfo.userId" class="user-info">
        <div class="info-item">
          <span class="info-label">User ID:</span>
          <span class="info-value font-mono text-xs">{{ userInfo.userId }}</span>
        </div>

        <div v-if="Object.keys(userInfo.properties).length > 0" class="properties-section">
          <h5 class="properties-title">User Properties</h5>
          <div class="properties-grid">
            <div
              v-for="[key, value] in Object.entries(userInfo.properties)"
              :key="key"
              class="property-item"
            >
              <span class="property-key">{{ key }}:</span>
              <span class="property-value">{{ value }}</span>
            </div>
          </div>
        </div>
      </div>

      <div v-else class="empty-state">
        <p>No user tracked yet</p>
        <p class="text-sm text-gray-500 mt-2">User will be tracked after login</p>
      </div>
    </div>

    <!-- Actions Section -->
    <div v-if="isModalOpen" class="modal-overlay" @click.self="closeIdentifyModal">
      <div class="modal-content">
        <div class="modal-header">
          <h4 class="modal-title">Manual User Identification</h4>
          <button class="close-button" @click="closeIdentifyModal">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              ></path>
            </svg>
          </button>
        </div>

        <div class="modal-body">
          <div class="identify-form">
            <div class="form-group">
              <label for="debug-user-id" class="form-label">User ID</label>
              <input
                id="debug-user-id"
                v-model="debugUserId"
                type="text"
                placeholder="e.g. user_123"
                class="form-input"
              />
            </div>
            <div class="form-group">
              <label for="debug-user-props" class="form-label">User Properties (JSON)</label>
              <textarea
                id="debug-user-props"
                v-model="debugUserProperties"
                rows="8"
                class="form-textarea font-mono text-xs"
              ></textarea>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button @click="closeIdentifyModal" class="action-button secondary">Cancel</button>
          <button
            @click="identifyUser"
            :disabled="isIdentifying || !debugUserId"
            class="action-button primary"
          >
            <svg
              v-if="isIdentifying"
              class="animate-spin -ml-1 mr-2 h-4 w-4"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              ></circle>
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            {{ isIdentifying ? 'Identifying...' : 'Identify User' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Actions Section -->
    <div class="config-section">
      <h4 class="section-title">Actions</h4>
      <div class="actions-row">
        <button
          @click="openIdentifyModal"
          :disabled="!status.isEnabled"
          class="action-button secondary"
        >
          <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
            ></path>
          </svg>
          Identify User...
        </button>

        <button
          @click="sendTestEvent"
          :disabled="isSendingTest || !status.isEnabled"
          class="action-button primary"
        >
          <svg
            v-if="isSendingTest"
            class="animate-spin -ml-1 mr-2 h-4 w-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              class="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="4"
            ></circle>
            <path
              class="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
          <svg v-else class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M13 10V3L4 14h7v7l9-11h-7z"
            ></path>
          </svg>
          {{ isSendingTest ? 'Sending...' : 'Send Test Event' }}
        </button>

        <button
          @click="clearEvents"
          :disabled="recentEvents.length === 0"
          class="action-button secondary"
        >
          <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
            ></path>
          </svg>
          Clear Events
        </button>
      </div>
    </div>

    <!-- Recent Events Section -->
    <div class="config-section">
      <h4 class="section-title">
        Recent Events
        <span class="value-count">({{ recentEvents.length }})</span>
      </h4>

      <div v-if="recentEvents.length === 0" class="empty-state">
        <p>No events tracked yet</p>
        <p class="text-sm text-gray-500 mt-2">Events will appear here as you use the app</p>
      </div>

      <div v-else class="events-list">
        <div
          v-for="(event, index) in recentEvents"
          :key="`${event.eventName}-${event.timestamp}-${index}`"
          class="event-item"
        >
          <div class="event-header">
            <span class="event-name font-mono font-semibold">{{ event.eventName }}</span>
            <span class="event-time text-xs text-gray-500">
              {{ formatTimestamp(event.timestamp) }}
            </span>
          </div>

          <div v-if="event.params && Object.keys(event.params).length > 0" class="event-params">
            <div v-for="[key, value] in Object.entries(event.params)" :key="key" class="param-item">
              <span class="param-key">{{ key }}:</span>
              <span class="param-value">{{ value }}</span>
            </div>
          </div>

          <div class="event-footer">
            <span class="event-full-time">{{ formatDate(event.timestamp) }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.analytics-debug {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.config-section {
  background: #f9fafb;
  border-radius: 8px;
  padding: 16px;
  border: 1px solid #e5e7eb;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #111827;
  margin: 0 0 16px 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.value-count {
  font-size: 14px;
  font-weight: 400;
  color: #6b7280;
}

.status-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
}

.status-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.status-label {
  font-size: 12px;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.status-value {
  font-size: 14px;
  font-weight: 500;
}

.warning-box {
  margin-top: 12px;
  padding: 12px;
  background: #fef3c7;
  border: 1px solid #fde68a;
  border-radius: 6px;
  color: #92400e;
  font-size: 14px;
}

.info-box {
  padding: 12px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 6px;
  color: #1e40af;
  font-size: 14px;
}

.info-box code {
  background: #dbeafe;
  padding: 2px 6px;
  border-radius: 4px;
}

.info-box a {
  color: #1d4ed8;
  text-decoration: underline;
}

.user-info {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.info-label {
  font-size: 12px;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.info-value {
  font-size: 14px;
  color: #111827;
}

.properties-section {
  margin-top: 8px;
}

.properties-title {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
  margin-bottom: 12px;
}

.properties-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 8px;
}

.property-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  font-size: 13px;
}

.property-key {
  font-weight: 500;
  color: #6b7280;
}

.property-value {
  color: #111827;
}

.actions-row {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.action-button {
  display: inline-flex;
  align-items: center;
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.action-button.primary {
  background: #3b82f6;
  color: white;
}

.action-button.primary:hover:not(:disabled) {
  background: #2563eb;
}

.action-button.secondary {
  background: #6b7280;
  color: white;
}

.action-button.secondary:hover:not(:disabled) {
  background: #4b5563;
}

.action-button:disabled {
  background: #d1d5db;
  cursor: not-allowed;
  opacity: 0.6;
}

.identify-form {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.form-label {
  font-size: 12px;
  font-weight: 500;
  color: #4b5563;
}

.form-input,
.form-textarea {
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.2s;
}

.form-input:focus,
.form-textarea:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
}

.w-full {
  width: 100%;
}

.justify-center {
  justify-content: center;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.modal-content {
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 500px;
  box-shadow:
    0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04);
  display: flex;
  flex-direction: column;
  max-height: 90vh;
}

.modal-header {
  padding: 16px 20px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.modal-title {
  font-size: 18px;
  font-weight: 600;
  color: #111827;
  margin: 0;
}

.close-button {
  color: #9ca3af;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.close-button:hover {
  color: #4b5563;
  background: #f3f4f6;
}

.modal-body {
  padding: 20px;
  overflow-y: auto;
}

.modal-footer {
  padding: 16px 20px;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.events-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: 500px;
  overflow-y: auto;
}

.event-item {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 12px;
}

.event-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.event-name {
  font-size: 14px;
  color: #3b82f6;
}

.event-time {
  font-family: monospace;
}

.event-params {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 8px 0;
  border-top: 1px solid #f3f4f6;
  border-bottom: 1px solid #f3f4f6;
  margin-bottom: 8px;
}

.param-item {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  padding: 4px 8px;
  background: #f3f4f6;
  border-radius: 4px;
}

.param-key {
  font-weight: 500;
  color: #6b7280;
}

.param-value {
  color: #111827;
  font-family: monospace;
}

.event-footer {
  font-size: 11px;
  color: #9ca3af;
}

.empty-state {
  text-align: center;
  padding: 32px;
  color: #6b7280;
}
</style>
